/**
 * useLeads Hook
 *
 * Every registered user is a lead. Unlike useUsers (which pages at ten rows
 * for the moderation table), this loads the whole `users` collection once and
 * filters in memory, so the admin can search, segment and export the entire
 * list in one go.
 */

import { useState, useEffect, useMemo } from 'react';
import { collection, query, orderBy, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '@/services/firebase';

export type LeadCategory = 'guest' | 'host' | 'admin';
export type LeadSource = 'app' | 'legacy';

export interface Lead {
  id: string;
  name: string;
  email: string;
  phone: string;
  category: LeadCategory;
  source: LeadSource;
  isSuspended: boolean;
  emailVerified: boolean;
  createdAt: Date;
  lastLoginAt?: Date;
}

export interface LeadsFilter {
  search: string;
  category: LeadCategory | '';
  source: LeadSource | '';
  /** Only leads that have a phone number on file. */
  withPhoneOnly: boolean;
}

export const EMPTY_LEADS_FILTER: LeadsFilter = {
  search: '',
  category: '',
  source: '',
  withPhoneOnly: false,
};

const toDate = (val: unknown): Date | undefined => {
  if (!val) return undefined;
  if (val instanceof Timestamp) return val.toDate();
  if (val instanceof Date) return val;
  const d = new Date(val as string);
  return isNaN(d.getTime()) ? undefined : d;
};

/**
 * Name resolution mirrors the users table: `displayName` when present, else
 * first + last, else fall back to the email so a row is never blank.
 */
const resolveName = (data: Record<string, any>): string => {
  const display = (data.displayName as string)?.trim();
  if (display) return display;
  const composed = `${data.firstName || ''} ${data.lastName || ''}`.trim();
  if (composed) return composed;
  return (data.email as string) || '';
};

/** Accounts carry the phone under a few different keys across signup paths. */
const resolvePhone = (data: Record<string, any>): string =>
  (data.phone || data.phoneNumber || data.mobile || '').toString().trim();

const mapLead = (id: string, data: Record<string, any>): Lead => ({
  id,
  name: resolveName(data),
  email: (data.email as string) || '',
  phone: resolvePhone(data),
  category: (data.role as LeadCategory) || 'guest',
  source: data.wpUserId ? 'legacy' : 'app',
  isSuspended: data.isSuspended === true,
  emailVerified: (data.isVerified ?? data.emailVerified) === true,
  createdAt: toDate(data.createdAt) || new Date(0),
  lastLoginAt: toDate(data.lastLoginAt),
});

export const useLeads = (filter: LeadsFilter) => {
  const [allLeads, setAllLeads] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchLeads = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const snap = await getDocs(
          query(collection(db, 'users'), orderBy('createdAt', 'desc'))
        );
        if (cancelled) return;
        setAllLeads(snap.docs.map((d) => mapLead(d.id, d.data())));
      } catch (err) {
        console.error('Failed to fetch leads:', err);
        if (!cancelled) {
          setAllLeads([]);
          setError('Could not load leads.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchLeads();
    return () => {
      cancelled = true;
    };
  }, []);

  const leads = useMemo(() => {
    let rows = allLeads;

    const search = filter.search.trim().toLowerCase();
    if (search) {
      rows = rows.filter(
        (l) =>
          l.name.toLowerCase().includes(search) ||
          l.email.toLowerCase().includes(search) ||
          l.phone.toLowerCase().includes(search)
      );
    }
    if (filter.category) rows = rows.filter((l) => l.category === filter.category);
    if (filter.source) rows = rows.filter((l) => l.source === filter.source);
    if (filter.withPhoneOnly) rows = rows.filter((l) => !!l.phone);

    return rows;
  }, [allLeads, filter.search, filter.category, filter.source, filter.withPhoneOnly]);

  const stats = useMemo(
    () => ({
      total: allLeads.length,
      hosts: allLeads.filter((l) => l.category === 'host').length,
      guests: allLeads.filter((l) => l.category === 'guest').length,
      withPhone: allLeads.filter((l) => !!l.phone).length,
    }),
    [allLeads]
  );

  return { leads, stats, isLoading, error };
};

export default useLeads;
